import React, { useState } from 'react';
import {
  Button,
  Input,
  Modal,
  notification,
  Radio,
  Select,
  Space,
  Typography,
} from '@duxton-web/ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';

import { VariableSettingsAction } from '@/features/variable/constants';
import {
  DISABLED_REASON_OPTIONS,
  mapVariableCanaryToConfig,
} from '@/features/variable/settings/canary';
import { ENABLE_CANARY_ON_OPEN_STATE } from '@/features/variable/settings/canary/enablement-utils';
import { getVariableEditPathname } from '@/features/variable/utils';
import { useUpdateVariable } from '@/hooks';
import { VariableInfo } from '@/lib/PlannerDTOs';

interface CanaryRolloutCheckModalProps {
  open: boolean;
  variable: VariableInfo;
  /** Called after the user picks "Continue without Canary" and the reason is
   *  successfully saved — triggers the normal Validate & Rollout flow. */
  onProceed: () => void;
  onClose: () => void;
}

const getBackendErrorMessage = (err: unknown): string | null => {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string') {
    return data.trim() || null;
  }
  if (!data || typeof data !== 'object') {
    return null;
  }

  const { devMessage, arg } = data as {
    devMessage?: unknown;
    arg?: unknown;
  };
  const messageParts = [devMessage, arg].filter(
    (part): part is string => typeof part === 'string' && Boolean(part.trim()),
  );
  return messageParts.map((part) => part.trim()).join(' ') || null;
};

const CanaryRolloutCheckModal: React.FC<CanaryRolloutCheckModalProps> = ({
  open,
  variable,
  onProceed,
  onClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [choice, setChoice] = useState<'enable' | 'skip' | null>(null);
  const [disabledReason, setDisabledReason] = useState<string>('');
  const [disabledReasonNote, setDisabledReasonNote] = useState<string>('');

  const { mutate: updateVariable, isPending } = useUpdateVariable({
    onSuccess: () => {
      handleReset();
      onProceed();
    },
    onError: (err: unknown) => {
      const backendMessage = getBackendErrorMessage(err);
      const actionableMessage = backendMessage
        ? `${backendMessage}${/[.!?]$/.test(backendMessage) ? '' : '.'} Please fix the issue and try again.`
        : 'Please try again.';
      notification.error({
        message: 'Failed to save canary reason',
        description: actionableMessage,
      });
    },
  });

  const handleReset = () => {
    setChoice(null);
    setDisabledReason('');
    setDisabledReasonNote('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const isSubmitDisabled =
    !choice ||
    (choice === 'skip' &&
      (!disabledReason ||
        (disabledReason === 'other' && !disabledReasonNote.trim())));

  const handleSubmit = () => {
    if (choice === 'enable') {
      handleClose();
      const canaryPath = getVariableEditPathname(
        location.pathname,
        variable.name,
        VariableSettingsAction.CANARY,
      );
      navigate(
        { ...location, pathname: canaryPath },
        {
          replace: true,
          state: {
            ...(location.state ?? {}),
            [ENABLE_CANARY_ON_OPEN_STATE]: true,
          },
        },
      );
      return;
    }

    // Save the reason then let the caller continue with the rollout flow.
    const currentCanaryConfig = mapVariableCanaryToConfig(variable);
    const updatedVariable: VariableInfo = {
      ...variable,
      metadata: {
        ...variable.metadata,
        canaryConfig: {
          ...currentCanaryConfig,
          enabled: false,
          disabledReason,
          ...(disabledReason === 'other'
            ? { disabledReasonNote: disabledReasonNote.trim() }
            : { disabledReasonNote: undefined }),
        },
      },
    };
    updateVariable(updatedVariable);
  };

  return (
    <Modal
      open={open}
      title="Reduce Rollout Risk with Canary Deployment"
      onCancel={handleClose}
      centered
      width={520}
      footer={[
        <Button key="cancel" size="small" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="small"
          disabled={isSubmitDisabled}
          loading={isPending}
          onClick={handleSubmit}
        >
          {choice === 'enable'
            ? 'Go to Canary Config'
            : 'Confirm & Validate Rollout'}
        </Button>,
      ]}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <InfoBox>
          <Typography.Text>
            Canary enables gradual rollouts with automatic monitoring and
            rollback capabilities — reducing risk of variable rollout updates.
            This variable is eligible for canary deployment but canary is
            currently disabled.
          </Typography.Text>
        </InfoBox>

        <Typography.Text strong>
          Would you like to enable canary rollout for this change?
        </Typography.Text>

        <Radio.Group
          value={choice}
          onChange={(e) => {
            setChoice(e.target.value);
            setDisabledReason('');
            setDisabledReasonNote('');
          }}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <OptionCard selected={choice === 'enable'}>
              <Radio value="enable">
                <Space direction="vertical" size={2}>
                  <Space size={8} align="center">
                    <Typography.Text strong>
                      Enable Canary Deployment
                    </Typography.Text>
                    <RecommendedBadge>Recommended</RecommendedBadge>
                  </Space>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12, fontWeight: 'normal' }}
                  >
                    I&apos;ll like to enable canary rollout with monitoring
                    before rolling out the change.
                  </Typography.Text>
                </Space>
              </Radio>
            </OptionCard>

            <OptionCard selected={choice === 'skip'}>
              <Radio value="skip">
                <Space direction="vertical" size={2}>
                  <Typography.Text strong>
                    Continue without Canary
                  </Typography.Text>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12, fontWeight: 'normal' }}
                  >
                    I&apos;ll take the risk &amp; proceed with a direct rollout
                    without canary
                  </Typography.Text>
                </Space>
              </Radio>
            </OptionCard>
          </Space>
        </Radio.Group>

        {choice === 'skip' && (
          <Space
            direction="vertical"
            size={8}
            style={{ width: '100%', paddingLeft: 24 }}
          >
            <Select
              placeholder="Select a reason for skipping canary"
              value={disabledReason || undefined}
              onChange={(val: string) => {
                setDisabledReason(val);
                setDisabledReasonNote('');
              }}
              options={DISABLED_REASON_OPTIONS}
              style={{ width: '100%' }}
            />
            {disabledReason === 'other' && (
              <Input.TextArea
                placeholder="Please describe why canary is not applicable (max 200 characters)"
                value={disabledReasonNote}
                onChange={(e) => setDisabledReasonNote(e.target.value)}
                maxLength={200}
                showCount
                rows={3}
              />
            )}
          </Space>
        )}
      </Space>
    </Modal>
  );
};

const InfoBox = styled.div`
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  padding: 10px 14px;
`;

const RecommendedBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #389e0d;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 4px;
  padding: 1px 6px;
  line-height: 18px;
`;

const OptionCard = styled.div<{ selected: boolean }>`
  border: 1px solid ${({ selected }) => (selected ? '#1677ff' : '#e8e8e8')};
  border-radius: 8px;
  padding: 12px 16px;
  width: 100%;
  background: ${({ selected }) => (selected ? '#e6f4ff' : 'transparent')};
  transition:
    border-color 0.2s,
    background 0.2s;

  .ant-radio-wrapper {
    width: 100%;
    align-items: flex-start;
  }
`;

export default CanaryRolloutCheckModal;
